import "../style.scss";

import type {
    AutocompleteProps,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
    FilterOptionsState,
} from "@mui/material";
import { Autocomplete, CircularProgress } from "@mui/material";
import ChannelIcon from "components/shared/ChannelIcon";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import InfoTooltip from "components/shared/InfoTooltip";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { IChannel } from "containers/Channels/types";
import { Body, SmallBody } from "helpers/TypographyComponents";
import { isObject } from "helpers/utils";
import type { HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TFetchStatus } from "store/types";

import variables from "../../../../assets/styles/_variables.module.scss";

export interface AutocompleteFormComponentProps extends Partial<AutocompleteProps<any, any, any, any>> {
    label?: ReactNode;
    options: any[];
    renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
    renderOptionText: (option: any) => string;
    renderOption?: (
        props: HTMLAttributes<HTMLLIElement>,
        option: any,
        state: AutocompleteRenderOptionState,
    ) => ReactNode;
    renderTags?: (tagValue: any[], getTagProps: AutocompleteRenderGetTagProps) => ReactNode;
    filterOptions?: (options: any[], state: FilterOptionsState<any>) => any[];
    defaultFilterOptions?: boolean;
    placeholder?: string;
    control?: any;
    controlName?: string;
    error?: any;
    required?: boolean;
    disabled?: boolean;
    disabledText?: string;
    addElement?: string;
    customRequiredMessage?: string;
    freeSolo?: boolean;
    isOptionEqualToValue?: (option: any, value: any) => boolean;
    onScrollEnd?: () => void;
    onScrollEndLoading?: TFetchStatus;
    disableClearable?: boolean;
    noOptionsText?: string;
    labelStyle?: React.CSSProperties;
    loading?: boolean;
    customValidate?: (value: string) => boolean;
    infoTooltip?: { text: string; link?: string; type: "info" | "question" };
    helpdeskInfo?: { text: string; link: string };
    helperText?: string;
    /**
     * Personnalise onClose (list) event
     */
    onClose?: () => void;
}

// now you can also give default mui autocomplete props
const AutocompleteFormComponent = forwardRef(function AutocompleteFormComponent(
    props: AutocompleteFormComponentProps,
    ref,
) {
    const {
        label,
        options,
        renderInput,
        renderOptionText,
        renderOption,
        defaultFilterOptions = false,
        placeholder,
        control,
        controlName,
        error,
        required,
        disabled = false,
        disabledText,
        addElement,
        customRequiredMessage,
        freeSolo,
        isOptionEqualToValue = (option: any, value: any) => option?.id == value?.id,
        onScrollEnd,
        onScrollEndLoading,
        disableClearable = false,
        noOptionsText,
        renderTags,
        labelStyle,
        loading,
        customValidate,
        infoTooltip,
        helpdeskInfo,
        helperText,
        onClose,
        ...rest
    } = props;
    const [t] = useTranslation();
    const filterOptions = !defaultFilterOptions
        ? {
              filterOptions: props.filterOptions ? props.filterOptions : (e: any) => e,
          }
        : {};

    const defaultRenderInput = (params: any) => (
        <StyledTextField
            {...params}
            error={!!error}
            helperText={helperText ?? error?.message ?? ""}
            placeholder={placeholder ?? ""}
            InputProps={{
                ...params.InputProps,
                endAdornment: (
                    <>
                        {loading ? <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} /> : null}
                        {params.InputProps.endAdornment}
                    </>
                ),
            }}
        />
    );

    const render = (field: ControllerRenderProps<FieldValues, string> | undefined = undefined) => {
        const selectedValue = field ? field.value : rest.value;
        const autocompleteValue = rest.multiple && !Array.isArray(selectedValue) ? [] : selectedValue;

        return !disabled ? (
            <>
                {label && (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <label className="form-component-label" htmlFor={controlName} style={labelStyle}>
                            {label}
                            {required && label && "*"}
                        </label>
                        {helpdeskInfo && (
                            <ExpandedHelpdeskIcon text={helpdeskInfo?.text} link={helpdeskInfo?.link} transparent />
                        )}
                        {infoTooltip && (
                            <InfoTooltip text={infoTooltip.text} iconType={infoTooltip.type} link={infoTooltip.link} />
                        )}
                    </div>
                )}
                <Autocomplete
                    defaultValue={rest.multiple ? [] : null} // fix console error, when is multiple default value should be undefined or array
                    {...field}
                    {...rest}
                    value={autocompleteValue}
                    ref={ref}
                    disableClearable={disableClearable}
                    onChange={(e, data, reason) => {
                        if (field && field.onChange) field.onChange(data);
                        if (rest?.onChange) rest.onChange(e, data, reason);
                    }}
                    onClose={onClose}
                    options={addElement ? [...options, { id: -1, addElement, name: addElement }] : options}
                    renderInput={renderInput ?? defaultRenderInput}
                    getOptionLabel={renderOptionText}
                    renderOption={(...args) =>
                        renderOption
                            ? renderOption(args[0], args[1], args[2])
                            : defaultRenderOption(args[0], args[1], args[2], renderOptionText)
                    }
                    renderTags={renderTags}
                    loading={loading}
                    loadingText={"Récupération des données..."}
                    freeSolo={freeSolo}
                    noOptionsText={
                        noOptionsText && !loading ? noOptionsText : !loading && t("AutocompleteForm.NoOptions")
                    }
                    {...filterOptions}
                    isOptionEqualToValue={isOptionEqualToValue}
                    componentsProps={{
                        popper: {
                            className: `autocomplete-form-component ${addElement ? "autocomplete-onscroll-add" : ""}`,
                        },
                    }}
                    ListboxProps={
                        onScrollEnd
                            ? {
                                  className:
                                      onScrollEndLoading == "pending"
                                          ? "autocomplete-onscrollend"
                                          : "autocomplete-list",
                                  onScroll: (event: React.SyntheticEvent) => {
                                      const listboxNode = event.currentTarget;
                                      const fullHeight = listboxNode.scrollTop + listboxNode.clientHeight;
                                      if (
                                          fullHeight > listboxNode.scrollHeight - 150 &&
                                          fullHeight < listboxNode.scrollHeight + 150
                                      ) {
                                          onScrollEnd();
                                      }
                                  },
                              }
                            : { className: "autocomplete-list" }
                    }
                />
            </>
        ) : (
            <>
                {label && (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <label className="form-component-label" htmlFor={controlName} style={labelStyle}>
                            {label}
                        </label>
                        {helpdeskInfo && (
                            <ExpandedHelpdeskIcon text={helpdeskInfo?.text} link={helpdeskInfo?.link} transparent />
                        )}
                        {infoTooltip && (
                            <InfoTooltip text={infoTooltip.text} iconType={infoTooltip.type} link={infoTooltip.link} />
                        )}
                    </div>
                )}
                <StyledTextField
                    {...field}
                    value={rest.multiple ? "" : renderOptionText(selectedValue)}
                    error={!!error}
                    name={controlName}
                    fullWidth
                    size={rest.size}
                    placeholder={placeholder ?? ""}
                    helperText={helperText ?? error?.message ?? ""}
                    disabled={disabled}
                    disabledText={disabledText}
                    ref={ref}
                />
            </>
        );
    };

    const validate = customValidate ? { validate: customValidate } : {};

    return (
        <div className="form-component">
            {control && controlName ? (
                <Controller
                    name={controlName}
                    control={control}
                    rules={{
                        required: required
                            ? customRequiredMessage
                                ? customRequiredMessage
                                : t("NewElement.Required")
                            : undefined,
                        ...validate,
                    }}
                    render={({ field }) => {
                        return render(field);
                    }}
                />
            ) : (
                render()
            )}
        </div>
    );
});

export default AutocompleteFormComponent;

/**
 * EXPORTED RENDER OPTIONS
 */

/**
 * Simple reusable render options, for one line formatted options
 * @param props mui autocomplete props
 * @param option current mui autocomplete option
 * @param optionNames object that take for key the value and has for value the translation name ex : { legal: Personne morale }
 */
export const renderOption = (props: any, option: string, optionNames?: Record<string, string>) => {
    return (
        <li {...props} key={`option-${option}`} className="country-market-option-list">
            <Body>{optionNames ? optionNames[option] : option}</Body>
        </li>
    );
};

export type TDefaultAutocompleteOption = {
    value: any;
    label: string;
    description?: string;
};

/**
 * Default render option if not function is passed.
 * @param props default props from Autocomplete
 * @param option option can be a simple string or an object of type {value: any, label: string}
 * @param state state within Autocomplete
 * @returns
 */
export const defaultRenderOption = <T extends string | TDefaultAutocompleteOption>(
    props: object,
    option: T,
    state: AutocompleteRenderOptionState,
    renderText?: (option: T) => string,
) => {
    const value = renderText?.(option);
    if (typeof option === "string") {
        return (
            <li {...props} key={`option-${option}`} className={`default-option ${state.selected ? "selected" : ""}`}>
                <Body>{option}</Body>
            </li>
        );
    } else if (isObject(option)) {
        return (
            <li
                {...props}
                key={`option-${option.value ?? value}`}
                className={`default-option ${state.selected ? "selected" : ""}`}
            >
                <Body>{option.label ?? value}</Body>
                {option?.description && (
                    <SmallBody color="grey" style={{ fontSize: "11px" }}>
                        {option.description}
                    </SmallBody>
                )}
            </li>
        );
    }
};

/**
 * Reusable render channels option
 * @param props mui autocomplete props
 * @param channel current option
 */
export const renderChannelsOption = (props: any, channel: IChannel) => {
    return (
        <li {...props} key={`channel-option-${channel.id}`} className="channels-option-list">
            <ChannelIcon channelKind={channel.kind} color="#B3B3B3" />
            <Body>{channel.name}</Body>
        </li>
    );
};
